import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import LoginPage from './pages/LoginPage.js';
import MagicLinkPage from './pages/MagicLinkPage.js';
import { LoginModalProvider } from './contexts/LoginModalContext.js';
import HomePage from './pages/HomePage.js';
import BusinessHomePage from './pages/BusinessHomePage.js';
import ContactPage from './pages/ContactForm.js';
import ContactUsPage from './pages/ContactUsPage.js';
import AppointmentsPage from './pages/AppointmentsPage.js';
import RegisterPage from './pages/RegisterPage.js';
import AdminPage from './pages/AdminPage.js';
import OrgBillingPage from './pages/OrgBillingPage.js';
import ProfilePage from './pages/ProfilePage.js';
import OrgBookingPage from './pages/OrgBookingPage.js';
import LegalIndexPage from './pages/LegalIndexPage.js';
import LegalDocPage from './pages/LegalDocPage.js';
import OnboardingAdminPage from './pages/OnboardingAdminPage.js';
import JoinPage from './pages/JoinPage.js';
import ResetPasswordPage from './pages/ResetPasswordPage.js';
import FeedbackButton from './components/FeedbackButton.js';

import { parseJwt, API_BASE } from './utils/api.js';

const theme = createTheme({
  palette: {
    mode: 'light',
    // Primary color is neutralized (blue is used for system primary),
    // but the main CTA uses a teal -> blue gradient defined below.
    primary: {
      main: '#007BFF',
      contrastText: '#ffffff',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      // Brand / headings / navigation text should use Deep Slate Blue
      primary: '#2D546E',
      secondary: '#6b7b84',
    },
    // Custom palette tokens for centralized theme usage in JS/TS
    custom: {
      teal: '#00C4CC',
      mint: '#E0F7F9',
      deepSlate: '#2D546E',
      gradientPrimary: 'linear-gradient(90deg, #00C4CC 0%, #007BFF 100%)',
    },
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    h4: { fontWeight: 700 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 600 },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
        },
        containedPrimary: {
          // vibrant teal -> blue gradient for main CTAs
          background: 'linear-gradient(90deg, #00C4CC 0%, #007BFF 100%)',
          color: '#ffffff !important',
          '&:hover': {
            // slightly darker hover gradient
            background: 'linear-gradient(90deg, #00A9B0 0%, #006FE6 100%)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        },
      },
    },
  },
});

function ProtectedRoute({ children }) {
  const token = localStorage.getItem('accessToken');
  return token ? children : <Navigate to="/login" replace />;
}

function AdminRoute({ children }) {
  const isStaff = localStorage.getItem('isStaff') === 'true';
  const groups = JSON.parse(localStorage.getItem('userGroups') || '[]');
  const isOrgAdmin = localStorage.getItem('isOrgAdmin') === 'true';
  return isStaff || groups.length > 0 || isOrgAdmin ? children : <Navigate to="/" replace />;
}

function App() {
  // Proactively attempt to refresh the access token when the app starts
  // and periodically (every 5 minutes) so users stay logged in as long as
  // possible without being interrupted by a 401. This uses the refresh
  // token stored in localStorage and updates accessToken + accessTokenExp.
  useEffect(() => {
    let mounted = true;
    const tryRefresh = async () => {
      const access = localStorage.getItem('accessToken');
      const expStr = localStorage.getItem('accessTokenExp');
      const now = Date.now();
      const fiveMin = 5 * 60 * 1000;

      // If access exists and is not expiring within 5 minutes, skip refresh
      if (access && expStr && Number(expStr) - now > fiveMin) return;

      try {
        // Try cookie-based refresh (backend will also accept JSON body for
        // backward compatibility). Use credentials so the HttpOnly cookie is sent.
        const res = await fetch(`${API_BASE}/token/refresh/`, {
          method: 'POST',
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          if (data.access) {
            localStorage.setItem('accessToken', data.access);
            const payload = parseJwt(data.access);
            if (payload && payload.exp) {
              localStorage.setItem('accessTokenExp', String(payload.exp * 1000));
            }
          }
          window.dispatchEvent(new Event('sqip:tokenRefreshed'));
        } else {
          // refresh failed - clear tokens to force re-login
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('accessTokenExp');
          window.dispatchEvent(new Event('sqip:loggedOut'));
        }
      } catch (e) {
        // ignore network errors here; we'll try again on next interval
      }
    };

    tryRefresh();
    const id = setInterval(tryRefresh, 5 * 60 * 1000);
    return () => { mounted = false; clearInterval(id); };
  }, []);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <LoginModalProvider>
          <Routes>
          {/* Public home page - no auth required */}
          <Route path="/" element={<HomePage />} />

            {/* Business Funnel */}
          <Route path="/business" element={<BusinessHomePage />} />
          <Route path="/business/form" element={<ContactPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/register/:reference" element={<RegisterPage />} />
          {/* Self-serve checkout entry (REACT_APP_IS_CHECKOUT=true "Join today" navigates here).
              Same page as /register; without this route the catch-all below bounced it home. */}
          <Route path="/checkout" element={<RegisterPage />} />
          <Route path="/checkout/:reference" element={<RegisterPage />} />

          {/* General "Contact us" page - public, emails support inbox */}
          <Route path="/contact" element={<ContactUsPage />} />


          {/* Legal pages - public, no auth required */}
          <Route path="/legal" element={<LegalIndexPage />} />
          <Route path="/legal/privacy" element={<LegalDocPage slug="privacy" />} />
          <Route path="/legal/terms" element={<LegalDocPage slug="terms" />} />
          <Route path="/legal/business-terms" element={<LegalDocPage slug="business-terms" />} />

          {/* Redirects from old legal URLs to the new ones (client-side).
              Route matching is case-insensitive, so -Tos / -ToS / -TOS all match. */}
          <Route path="/legal/user-Tos" element={<Navigate to="/legal/terms" replace />} />
          <Route path="/legal/business-Tos" element={<Navigate to="/legal/business-terms" replace />} />

          
          {/* Login page */}
          <Route path="/login" element={<LoginPage />} />
          {/* Passwordless admin-login magic link (from onboarding/checkout emails) */}
          <Route path="/login/magic/:token" element={<MagicLinkPage />} />

          {/* Admin password reset (link emailed by forgot-password / sqip admin) */}
          <Route path="/reset-password/:uid/:token" element={<ResetPasswordPage />} />
          
          <Route path="/onboarding-admin-page" element={<OnboardingAdminPage />} />
          {/* Public org/category/appointment pages for browsing (slug-based, id-free URLs) */}
          <Route path="/org/:orgSlug" element={<OrgBookingPage />} />
          <Route path="/org/:orgSlug/category/:catSlug" element={<OrgBookingPage />} />
          <Route path="/org/:orgSlug/facade/:facadeSlug" element={<OrgBookingPage />} />
          {/* Booking-invite deep link: consume the token, then land on the target queue */}
          <Route path="/join/:token" element={<JoinPage />} />
          <Route path="/appointment/:appointmentId" element={<AppointmentsPage />} />
          
          {/* Protected routes - require authentication */}
          <Route
            path="/appointments"
            element={
              <ProtectedRoute>
                <AppointmentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminPage />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/billing"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <OrgBillingPage />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          {/* Floating feedback button — logged-in users only, behind REACT_APP_FEEDBACK_ENABLED */}
          <FeedbackButton />
        </LoginModalProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
